import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getInventory, createOrder, getCustomers, createCustomer } from '../services/api';
import renderA4Invoice from '../utils/renderA4Invoice';
import printHtml from '../utils/printHtml';

// Red Sea Transport POS — single-screen service entry.
// Customer required (no walk-in). Services as table rows with a big
// quantity input; price defaults to K1 so the quantity carries the
// kwacha amount. One Save button fiscalises with ZRA and prints the
// A4 invoice. Dark theme, amber accent, matches the login identity.

const TransportPOS = () => {
  const { user } = useAuth();

  // customers
  const [customers, setCustomers] = useState([]);
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // services (filtered inventory)
  const [services, setServices] = useState([]);

  // sale rows
  const [rows, setRows] = useState([blankRow()]);
  const [notes, setNotes] = useState('');

  // ui state
  const [saving, setSaving] = useState(false);
  // 2026-10-20 — fiscalising overlay. Mirrors the Red Sea POS pattern:
  // during saveOrder, show a full-screen amber spinner + ticking seconds
  // counter so the operator sees that ZRA is being contacted. {at: Date.now()}
  const [fiscalising, setFiscalising] = useState(null);
  const [fiscalElapsed, setFiscalElapsed] = useState(0);
  useEffect(() => {
    if (!fiscalising) { setFiscalElapsed(0); return; }
    const id = setInterval(() => {
      setFiscalElapsed(((Date.now() - fiscalising.at) / 1000));
    }, 100);
    return () => clearInterval(id);
  }, [fiscalising]);
  const [toast, setToast] = useState(null);
  const [zraOnline, setZraOnline] = useState(true);
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 720);

  // New-customer modal
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCust, setNewCust] = useState(blankCustomer());
  const [newCustSaving, setNewCustSaving] = useState(false);

  const custInputRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    getCustomers().then(r => setCustomers(r.data || [])).catch(() => {});
    getInventory().then(r => {
      // 2026-10-20 — Transport is a separate deployment with its own DB,
      // so Distribution products can't leak in. The old name-regex filter
      // was unneeded and hid legitimate services whose names didn't match
      // "service|claim|fee|levy|rental" (e.g. "Rent for 3 months").
      setServices(r.data || []);
    }).catch(() => {});
  }, []);

  const customerSuggestions = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return customers.slice(0, 10);
    return customers
      .filter(c => (c.name || '').toLowerCase().includes(q) || (c.tpin || '').includes(q))
      .slice(0, 10);
  }, [customers, customerQuery]);

  // 2026-10-20 — Compute VAT per line from each service's ZRA category.
  // Prior behaviour divided the whole sale by 1.16, which charged 16%
  // VAT on D-Exempt services (e.g. Offloading Fee) both on screen and
  // on the A4 invoice reprint. ZRA itself was fine (vsdcClient recomputes
  // from category) but the customer-facing paper lied. Categories that
  // carry a VAT rate per the backend VAT_RATES table (vsdcClient.js:447)
  // are A (16) and B (16 on RRP); everything else is zero.
  const vatRateForCat = (cat) => {
    const c = String(cat || 'A').toUpperCase();
    return (c === 'A' || c === 'B' || c === 'RVAT') ? 16 : 0;
  };
  const lineBreakdown = (r) => {
    const svc = services.find(s => String(s.id) === String(r.serviceId));
    const cat = svc?.zra_vat_cat_cd || svc?.vat_category || 'A';
    const rate = vatRateForCat(cat);
    const incl = lineTotal(r);
    const vatAmt = rate > 0 ? incl - incl / (1 + rate / 100) : 0;
    return { cat, rate, incl, taxable: incl - vatAmt, vat: vatAmt };
  };
  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      const b = lineBreakdown(r);
      acc.taxable += b.taxable;
      acc.vat     += b.vat;
      acc.grand   += b.incl;
      return acc;
    }, { taxable: 0, vat: 0, grand: 0 });
  }, [rows, services]);
  const subtotal = totals.grand;
  const taxable  = totals.taxable;
  const vat      = totals.vat;
  const grand    = totals.grand;

  const canSave = selectedCustomer && rows.some(r => r.serviceId && lineTotal(r) > 0) && !saving;
  // Only allow adding a new row when EVERY existing row is filled.
  // Prevents a column of empty rows that confuses the operator.
  const canAddRow = rows.every(r => r.serviceId && Number(r.quantity) > 0);

  const updateRow = (idx, patch) => {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const removeRow = (idx) => {
    setRows(rs => rs.length === 1 ? [blankRow()] : rs.filter((_, i) => i !== idx));
  };
  const addRow = () => setRows(rs => [...rs, blankRow()]);

  const resetSale = () => {
    setRows([blankRow()]);
    setSelectedCustomer(null);
    setCustomerQuery('');
    setNotes('');
  };

  const openNewCustomer = () => {
    setNewCust({ ...blankCustomer(), name: customerQuery.trim() });
    setNewCustOpen(true);
  };
  const saveNewCustomer = async () => {
    if (!newCust.name.trim()) {
      setToast({ kind: 'err', msg: 'Customer name is required.' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    setNewCustSaving(true);
    try {
      const res = await createCustomer(newCust);
      const created = res.data || { ...newCust, id: Date.now() };
      setCustomers(prev => [created, ...prev]);
      setSelectedCustomer(created);
      setCustomerQuery('');
      setNewCustOpen(false);
      setNewCust(blankCustomer());
      setToast({ kind: 'ok', msg: `Customer "${created.name}" added.` });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setToast({ kind: 'err', msg: err?.response?.data?.error || 'Could not save customer.' });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setNewCustSaving(false);
    }
  };

  // 2026-10-20 — Print preview. Builds the A4 HTML from the current
  // cart WITHOUT creating an order, so the operator can see the layout
  // (and show the customer) before committing. Prints as 'copy' so the
  // paper is clearly marked "TAX INVOICE COPY" and nothing on it looks
  // fiscal (no ZRA receipt no, no QR). Also skipped if there is no
  // customer picked or every row is empty.
  const handlePreview = async () => {
    if (!selectedCustomer) {
      setToast({ kind: 'err', msg: 'Pick a customer first.' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    const cart = rows.filter(r => r.serviceId && lineTotal(r) > 0).map(r => {
      const svc = services.find(s => String(s.id) === String(r.serviceId));
      const qty = Number(r.quantity) || 0;
      const prc = Number(r.price) || 0;
      const lineTot = qty * prc;
      const b = lineBreakdown(r);
      return {
        product_name: svc?.name || 'Service',
        quantity: qty,
        unit_price: prc,
        line_total: lineTot,
        discount: 0,
        zra_vat_cat_snap:  b.cat,
        zra_vat_rate:      b.rate,
        zra_vat_taxbl_amt: b.taxable,
        zra_vat_amt:       b.vat,
      };
    });
    if (cart.length === 0) {
      setToast({ kind: 'err', msg: 'Add at least one service row before preview.' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    try {
      const html = await renderA4Invoice({
        variant: 'copy',
        business: {
          business_name: 'Red Sea Transport',
          business_phone: '',
          business_address: '',
          zra_tpin: '1001710705',
          zra_bhf_id: '063',
        },
        order: {
          order_number: 'PREVIEW',
          created_at: new Date(),
          served_by: user ? (`${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || user.name || user.email || 'Operator') : 'Operator',
          customer_name: selectedCustomer.name,
          buyer_tpin: selectedCustomer.tpin,
          notes,
        },
        items: cart,
        customer: {
          name: selectedCustomer.name,
          email: selectedCustomer.email || '',
          address: selectedCustomer.address || '',
          tpin: selectedCustomer.tpin,
          phone: selectedCustomer.phone || '',
        },
        payment: { cash: grand, momo: 0, bank: 0, received: grand, change: 0 },
        curSym: 'K ',
      });
      printHtml(html);
    } catch (_) { /* best-effort */ }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setFiscalising({ at: Date.now() });
    try {
      const cart = rows.filter(r => r.serviceId && lineTotal(r) > 0).map(r => {
        const svc = services.find(s => String(s.id) === String(r.serviceId));
        const qty = Number(r.quantity) || 0;
        const prc = Number(r.price) || 0;
        const lineTot = qty * prc;
        const b = lineBreakdown(r);
        return {
          product_id: svc?.id,
          product_name: svc?.name || 'Service',
          quantity: qty,
          price: prc,
          unit_price: prc,
          total_price: lineTot,       // required by order_items NOT NULL
          line_total: lineTot,
          total_amount: lineTot,
          subtotal: lineTot,
          discount: 0,
          cost_price: 0,
          vat_category:         b.cat,
          zra_vat_cat_snap:     b.cat,
          zra_vat_rate:         b.rate,
          zra_vat_taxbl_amt:    b.taxable,
          zra_vat_amt:          b.vat,
        };
      });

      const res = await createOrder({
        customer_name: selectedCustomer.name,
        customer_id: selectedCustomer.id,
        customer_sync_id: selectedCustomer.sync_id,
        customer_tpin: selectedCustomer.tpin || null,
        customer_address: selectedCustomer.address || null,
        items: cart,
        subtotal: taxable,
        tax_amount: vat,
        total_amount: grand,
        discount: 0,
        amount_received: grand,
        cash_received: grand,
        momo_received: 0,
        bank_received: 0,
        change_amount: 0,
        payment_method: 'Cash',
        currency: 'K',
        notes,
      });

      const data = res.data || {};
      setToast({ kind: 'ok', msg: `Saved · ${data.order_number || 'OK'}${data.zra_status === 'SIGNED' ? ' · Fiscalised' : ' · Awaiting ZRA'}` });

      // Fire print asynchronously so the UI stays responsive
      try {
        const html = await renderA4Invoice({
          variant: 'original',
          business: {
            business_name: 'Red Sea Transport',
            business_phone: '',
            business_address: '',
            zra_tpin: data.tpin || data.zra_tpin || '1001710705',
            zra_sdc_id: data.zra_sdc_id,
            zra_dvc_srl_no: data.zra_dvc_srl_no,
            zra_bhf_id: data.zra_bhf_id,
          },
          order: {
            order_number: data.order_number,
            zra_cis_invc_no: data.zra_cis_invc_no,
            zra_rcpt_no: data.zra_rcpt_no,
            zra_sdc_id: data.zra_sdc_id,
            zra_intrl_data: data.zra_intrl_data,
            zra_rcpt_sign: data.zra_rcpt_sign,
            zra_vsdc_rcpt_pbct_date: data.zra_vsdc_rcpt_pbct_date,
            created_at: new Date(),
            served_by: user ? (`${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || user.name || user.email || 'Operator') : 'Operator',
            customer_name: selectedCustomer.name,
            buyer_tpin: selectedCustomer.tpin,
            notes,
          },
          items: cart,
          customer: {
            name: selectedCustomer.name,
            email: selectedCustomer.email || '',
            address: selectedCustomer.address || '',
            tpin: selectedCustomer.tpin,
            phone: selectedCustomer.phone || '',
          },
          payment: { cash: grand, momo: 0, bank: 0, received: grand, change: 0 },
          curSym: 'K ',
          qrDataUrl: data.zra_qr_code_url || '',
        });
        printHtml(html);
      } catch (_) { /* print best-effort */ }

      resetSale();
      setTimeout(() => setToast(null), 4500);
    } catch (err) {
      setToast({ kind: 'err', msg: err?.response?.data?.error || 'Could not save the sale. Try again.' });
      setTimeout(() => setToast(null), 6000);
    } finally {
      setSaving(false);
      setFiscalising(null);
    }
  };

  const now = useMemo(() => {
    const d = new Date();
    const pad = n => String(n).padStart(2, '0');
    return `${d.getDate()} ${d.toLocaleString('en', { month: 'short' })} ${d.getFullYear()} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  return (
    <div style={{...S.page, padding: isNarrow ? '14px 14px 40px' : '20px 28px'}}>

      {/* 2026-10-20 — Fiscalising overlay (ported from Red Sea main POS).
          Blocks the UI while the backend talks to ZRA so the operator sees
          something is happening and doesn't double-tap Save. Amber accent
          matches the Transport theme. Elapsed counter ticks every 100ms. */}
      {fiscalising && (
        <div
          onClick={e => { e.stopPropagation(); e.preventDefault(); }}
          onContextMenu={e => e.preventDefault()}
          style={{
            position: 'fixed', inset: 0, zIndex: 100000,
            background: 'rgba(5, 7, 10, 0.78)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'wait', userSelect: 'none',
          }}
        >
          <div style={{
            background: '#141924', borderRadius: 16, padding: '32px 40px',
            minWidth: 340, textAlign: 'center',
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.65)',
            border: '1px solid #2a3344',
          }}>
            <div style={{
              width: 44, height: 44, margin: '0 auto 18px',
              border: '4px solid rgba(245, 158, 11, 0.2)',
              borderTopColor: '#f59e0b',
              borderRadius: '50%',
              animation: 'rtFiscalSpin 0.9s linear infinite',
            }} />
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.2, color: '#e8e6e0', textTransform: 'uppercase' }}>
              Fiscalising with ZRA
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: '#f59e0b', margin: '10px 0 4px', fontVariantNumeric: 'tabular-nums' }}>
              {fiscalElapsed.toFixed(1)}s
            </div>
            <div style={{ fontSize: 12, color: '#94a1b8', lineHeight: 1.6 }}>
              Sale is saved. Waiting for the fiscal receipt number.
            </div>
            {fiscalElapsed >= 5 && (
              <div style={{
                marginTop: 14, padding: '8px 12px', borderRadius: 8,
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.35)',
                fontSize: 11, color: '#f59e0b', lineHeight: 1.6,
              }}>
                Taking longer than usual — the sale is safe, still waiting for ZRA to respond.
              </div>
            )}
          </div>
          <style>{`@keyframes rtFiscalSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      <div style={S.topbar}>
        <h2 style={S.h2}>New Sale</h2>
        <div style={S.topRight}>
          <div style={S.status}>
            <span style={{...S.statusDot, background: zraOnline ? T.ok : T.warn}} />
            ZRA {zraOnline ? 'Online' : 'Offline'}
          </div>
          <span style={{fontFamily: T.mono, fontSize: 12}}>{now}</span>
        </div>
      </div>

      {toast && (
        <div style={{...S.toast, ...(toast.kind === 'ok' ? S.toastOk : S.toastErr)}}>
          {toast.msg}
        </div>
      )}

      <div style={{...S.card, padding: isNarrow ? '16px 14px' : '22px 24px'}}>

        {/* Customer */}
        <div style={{marginBottom: 14}}>
          <label style={S.lbl}>Customer <span style={S.req}>● REQUIRED</span></label>
          <div style={{position: 'relative'}}>
            <input
              ref={custInputRef}
              style={S.input}
              placeholder="Type to search customer by name or TPIN…"
              value={selectedCustomer ? selectedCustomer.name : customerQuery}
              onChange={e => { setSelectedCustomer(null); setCustomerQuery(e.target.value); setCustomerDropdownOpen(true); }}
              onFocus={() => setCustomerDropdownOpen(true)}
              onBlur={() => setTimeout(() => setCustomerDropdownOpen(false), 180)}
            />
            {selectedCustomer && (
              <button type="button" onClick={() => { setSelectedCustomer(null); setCustomerQuery(''); custInputRef.current?.focus(); }} style={S.clearBtn}>×</button>
            )}
            {customerDropdownOpen && !selectedCustomer && (
              <div style={S.dropdown}>
                {customerSuggestions.map(c => (
                  <div key={c.id} style={S.dropItem}
                    onMouseDown={() => { setSelectedCustomer(c); setCustomerQuery(''); setCustomerDropdownOpen(false); }}>
                    <div style={{fontWeight: 600, color: T.ink}}>{c.name}</div>
                    <div style={S.dropMeta}>TPIN {c.tpin || '—'} · {c.phone || 'no phone'}</div>
                  </div>
                ))}
                {customerSuggestions.length === 0 && (
                  <div style={{...S.dropItem, color: T.inkMuted, fontStyle: 'italic'}}>No match — type a different name or TPIN.</div>
                )}
                {/* 2026-10-20 — + Add Customer back inside the dropdown as a
                    sticky bottom row (like the earlier Transport build).
                    Keeps the main search field full-width and puts creation
                    one keystroke away without a second visible button. */}
                <div
                  onMouseDown={e => { e.preventDefault(); setCustomerDropdownOpen(false); openNewCustomer(); }}
                  style={{...S.dropItem, borderTop: `1px solid ${T.border}`, color: T.accent, fontWeight: 700, cursor: 'pointer'}}
                >
                  + Add Customer
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedCustomer && (
          <div style={S.row2}>
            <div><label style={S.lbl}>Customer TPIN</label>
              <input style={{...S.input, ...S.inputReadonly}} value={selectedCustomer.tpin || ''} readOnly /></div>
            <div><label style={S.lbl}>Phone / Contact</label>
              <input style={{...S.input, ...S.inputReadonly}} value={selectedCustomer.phone || ''} readOnly /></div>
          </div>
        )}

        {/* Service rows — table on desktop, cards on phone */}
        {isNarrow ? (
          <div style={{marginTop: 18, display: 'flex', flexDirection: 'column', gap: 12}}>
            {rows.map((r, i) => (
              <div key={i} style={S.svcCard}>
                <div style={S.svcCardHdr}>
                  <span style={S.svcCardIdx}>Service #{i + 1}</span>
                  <button type="button" onClick={() => removeRow(i)} style={S.rmBtn} title="Remove">×</button>
                </div>
                <label style={S.lbl}>Service</label>
                <select style={{...S.cellInput, marginBottom: 10}} value={r.serviceId} onChange={e => {
                  const svc = services.find(s => String(s.id) === e.target.value);
                  updateRow(i, { serviceId: e.target.value, price: svc?.selling_price || 1 });
                }}>
                  <option value="" style={S.optStyle}>— pick service —</option>
                  {services.map(s => <option key={s.id} value={s.id} style={S.optStyle}>{s.name}</option>)}
                </select>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10}}>
                  <div>
                    <label style={S.lbl}>Quantity</label>
                    <input style={S.qtyInput} value={fmtQty(r.quantity)}
                      onChange={e => updateRow(i, { quantity: sanitizeNumber(e.target.value) })} placeholder="0" inputMode="decimal"/>
                  </div>
                  <div>
                    <label style={S.lbl}>Price (K)</label>
                    <input style={{...S.priceInput, ...S.inputReadonly, cursor: 'not-allowed'}} value={r.price}
                      readOnly tabIndex={-1} title="Price is set on the service record and cannot be edited here" inputMode="decimal"/>
                  </div>
                </div>
                <div style={S.svcCardTotal}>
                  <span style={S.tLbl}>Line Total</span>
                  <span style={S.lineTotalCell}>K {fmt(lineTotal(r))}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={S.svcTable}>
            <thead>
              <tr>
                <th style={{...S.svcTh, width: '36%'}}>Service</th>
                <th style={{...S.svcTh, width: '22%', textAlign: 'right'}}>Quantity</th>
                <th style={{...S.svcTh, width: '16%', textAlign: 'right'}}>Price (K)</th>
                <th style={{...S.svcTh, width: '22%', textAlign: 'right'}}>Line Total</th>
                <th style={{...S.svcTh, width: 40}}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={S.svcTd}>
                    <select style={S.cellInput} value={r.serviceId} onChange={e => {
                      const svc = services.find(s => String(s.id) === e.target.value);
                      updateRow(i, { serviceId: e.target.value, price: svc?.selling_price || 1 });
                    }}>
                      <option value="" style={S.optStyle}>— pick service —</option>
                      {services.map(s => <option key={s.id} value={s.id} style={S.optStyle}>{s.name}</option>)}
                    </select>
                  </td>
                  <td style={{...S.svcTd, textAlign: 'right'}}>
                    <input style={S.qtyInput} value={fmtQty(r.quantity)}
                      onChange={e => updateRow(i, { quantity: sanitizeNumber(e.target.value) })} placeholder="0"/>
                  </td>
                  <td style={{...S.svcTd, textAlign: 'right'}}>
                    <input style={{...S.priceInput, ...S.inputReadonly, cursor: 'not-allowed'}} value={r.price}
                      readOnly tabIndex={-1} title="Price is set on the service record and cannot be edited here"/>
                  </td>
                  <td style={{...S.svcTd, ...S.lineTotalCell}}>
                    {fmt(lineTotal(r))}
                  </td>
                  <td style={S.svcTd}>
                    <button type="button" onClick={() => removeRow(i)} style={S.rmBtn} title="Remove row">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <button type="button" onClick={addRow} disabled={!canAddRow}
          style={{...S.addBtn, ...(canAddRow ? {} : S.addBtnDisabled)}}
          title={canAddRow ? '' : 'Fill the current service row first (pick a service and type a quantity)'}>
          + Add another service
        </button>

        <div style={{marginTop: 18}}>
          <label style={S.lbl}>Notes / Reference</label>
          <input style={S.input} placeholder="LPO number, delivery ref, vehicle reg, etc."
            value={notes} onChange={e => setNotes(e.target.value)} />
        </div>

        {/* Footer */}
        <div style={S.footerRow}>
          <div style={S.totals}>
            <div><span style={S.tLbl}>SUB TOTAL (EX VAT)</span><span style={S.tVal}>{fmt(taxable)}</span></div>
            <div><span style={S.tLbl}>VAT 16%</span><span style={S.tVal}>{fmt(vat)}</span></div>
            <div style={S.grand}>
              <div style={S.grandLbl}>TOTAL DUE</div>
              ZMW {fmt(grand)}
            </div>
          </div>
          <div style={{display: 'flex', gap: 10, flexDirection: isNarrow ? 'column' : 'row', alignItems: 'stretch'}}>
            <button type="button" onClick={handlePreview} disabled={!selectedCustomer || saving}
              title="Open the invoice layout for review — nothing is saved"
              style={{
                background: 'transparent', color: T.accent,
                border: `1px solid ${T.accent}`, borderRadius: 10,
                padding: '14px 22px', fontSize: 14, fontWeight: 700,
                letterSpacing: 1, cursor: (!selectedCustomer || saving) ? 'not-allowed' : 'pointer',
                opacity: (!selectedCustomer || saving) ? 0.5 : 1,
                fontFamily: T.sans, textTransform: 'uppercase',
              }}>
              PREVIEW
            </button>
            <button type="button" onClick={handleSave} disabled={!canSave} style={{...S.saveBtn, ...(canSave ? {} : S.saveBtnDisabled), ...(isNarrow ? {width:'100%', padding:'16px'} : {})}}>
              {saving ? 'SAVING…' : 'SAVE'}
              {!saving && <span style={S.saveAmt}>K {fmt(grand)}</span>}
            </button>
          </div>
        </div>

        {!selectedCustomer && (
          <div style={S.hintBox}>Pick a customer to enable the Save button. Walk-in sales are not permitted on this terminal.</div>
        )}
      </div>

      {/* ─── New Customer modal ─── */}
      {newCustOpen && (
        <div style={S.modalOverlay} onClick={() => !newCustSaving && setNewCustOpen(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <div style={{fontSize: 17, fontWeight: 600, color: T.ink}}>New Customer</div>
              <button type="button" onClick={() => setNewCustOpen(false)} style={S.modalX}>×</button>
            </div>

            <div style={S.modalBody}>
              <div style={{marginBottom: 12}}>
                <label style={S.lbl}>Name <span style={S.req}>● REQUIRED</span></label>
                <input style={S.input} autoFocus value={newCust.name}
                  onChange={e => setNewCust({...newCust, name: e.target.value})}
                  placeholder="e.g. SAFEMORE ZAMBIA LTD"/>
              </div>

              <div style={S.row2}>
                <div>
                  <label style={S.lbl}>Type</label>
                  <select style={S.input} value={newCust.type}
                    onChange={e => setNewCust({...newCust, type: e.target.value})}>
                    <option style={S.optStyle} value="Regular">Regular</option>
                    <option style={S.optStyle} value="Wholesale">Wholesale</option>
                    <option style={S.optStyle} value="Retail">Retail</option>
                  </select>
                </div>
                <div>
                  <label style={S.lbl}>Phone</label>
                  <input style={S.input} value={newCust.phone}
                    onChange={e => setNewCust({...newCust, phone: e.target.value})}
                    placeholder="e.g. 0977 123 456"/>
                </div>
              </div>

              <div style={{marginBottom: 12}}>
                <label style={S.lbl}>Email</label>
                <input style={S.input} value={newCust.email}
                  onChange={e => setNewCust({...newCust, email: e.target.value})}
                  placeholder="optional"/>
              </div>

              <div style={{marginBottom: 12}}>
                <label style={S.lbl}>Address</label>
                <input style={S.input} value={newCust.address}
                  onChange={e => setNewCust({...newCust, address: e.target.value})}
                  placeholder="Street, city"/>
              </div>

              <div style={{marginBottom: 12}}>
                <label style={S.lbl}>Buyer TPIN <span style={{color: T.inkMuted, textTransform: 'none', letterSpacing: 0, fontSize: 10, fontWeight: 500}}>— 10-digit, leave blank for walk-in</span></label>
                <input style={S.input} value={newCust.tpin}
                  onChange={e => setNewCust({...newCust, tpin: e.target.value})}
                  placeholder="e.g. 1001710705"/>
              </div>

              <div style={S.row2}>
                <div>
                  <label style={S.lbl}>Credit Limit (K) <span style={{color: T.inkMuted, textTransform: 'none', letterSpacing: 0, fontSize: 10, fontWeight: 500}}>— 0 = cash only</span></label>
                  <input style={S.input} value={newCust.credit_limit}
                    onChange={e => setNewCust({...newCust, credit_limit: e.target.value})}
                    placeholder="0"/>
                </div>
                <div>
                  <label style={S.lbl}>Payment Terms (days)</label>
                  <input style={S.input} value={newCust.payment_terms_days}
                    onChange={e => setNewCust({...newCust, payment_terms_days: e.target.value})}
                    placeholder="0"/>
                </div>
              </div>
            </div>

            <div style={S.modalFoot}>
              <button type="button" onClick={() => setNewCustOpen(false)} disabled={newCustSaving} style={S.btnSecondary}>Cancel</button>
              <button type="button" onClick={saveNewCustomer} disabled={newCustSaving || !newCust.name.trim()}
                style={{...S.btnPrimary, ...(newCustSaving || !newCust.name.trim() ? S.btnDisabled : {})}}>
                {newCustSaving ? 'SAVING…' : 'SAVE'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// ------- helpers -------
const blankRow = () => ({ serviceId: '', quantity: '', price: 1 });
const blankCustomer = () => ({
  name: '', type: 'Regular', phone: '', email: '', address: '',
  tpin: '', credit_limit: '', payment_terms_days: '', credit_status: 'Active',
});
const lineTotal = (r) => (Number(r.quantity) || 0) * (Number(r.price) || 0);
const sanitizeNumber = (v) => String(v).replace(/[^0-9.]/g, '');
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Display helper for the Quantity input: inserts thousand separators
// while the user types. sanitizeNumber already strips commas on input,
// so this is purely a display concern.
const fmtQty = (v) => {
  if (v === '' || v == null) return '';
  const s = String(v);
  const [whole, frac] = s.split('.');
  const withCommas = Number(whole || 0).toLocaleString();
  return frac !== undefined ? `${withCommas}.${frac}` : withCommas;
};

// ------- design tokens -------
const T = {
  bg:'#0b0f17', surface1:'#141924', surface2:'#1c2230', surface3:'#263040',
  border:'#2a3344', borderStrong:'#3b465c',
  ink:'#e8e6e0', inkDim:'#94a1b8', inkMuted:'#5f6d85',
  accent:'#f59e0b', accentGlow:'#fbbf24', accentInk:'#0b0f17',
  ok:'#10b981', warn:'#f87171',
  // Use Inter everywhere — matches the rest of the app chrome and
  // avoids the "AI-generated" feel of the Space Grotesk display.
  display:"'Inter',-apple-system,'Segoe UI',sans-serif",
  sans:"'Inter',-apple-system,'Segoe UI',sans-serif",
  // Numbers stay in Inter too — tabular-nums keeps columns aligned
  // without the "console" look of JetBrains Mono.
  mono:"'Inter',-apple-system,'Segoe UI',sans-serif",
};

const S = {
  page: { padding: '20px 28px', minHeight: '100%', height: '100%', background: T.bg, color: T.ink, fontFamily: T.sans, boxSizing: 'border-box', overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
  topbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  h2: { fontFamily: T.display, fontSize: 22, fontWeight: 600, margin: 0, color: T.ink },
  topRight: { display: 'flex', alignItems: 'center', gap: 16, color: T.inkDim },
  status: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5 },
  statusDot: { width: 8, height: 8, borderRadius: '50%', boxShadow: '0 0 0 3px rgba(16,185,129,0.2)' },

  toast: { padding: '10px 16px', borderRadius: 6, marginBottom: 14, fontSize: 13, fontWeight: 500 },
  toastOk: { background: 'rgba(16,185,129,0.1)', color: T.ok, border: `1px solid ${T.ok}` },
  toastErr:{ background: 'rgba(248,113,113,0.1)', color: T.warn, border: `1px solid ${T.warn}` },

  card: { background: T.surface1, border: `1px solid ${T.border}`, borderRadius: 8, padding: '22px 24px' },

  lbl: { display: 'block', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.8, color: T.inkDim, fontWeight: 600, marginBottom: 6 },
  req: { color: T.accent, marginLeft: 4, fontSize: 9, letterSpacing: 1 },

  input: { width: '100%', background: T.surface2, color: T.ink, border: `1px solid ${T.border}`, borderRadius: 5, padding: '10px 12px', fontSize: 13.5, fontFamily: T.sans, outline: 'none', boxSizing: 'border-box' },
  inputReadonly: { background: T.surface3, color: T.inkDim },

  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 },

  clearBtn: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'transparent', color: T.inkMuted, border: 'none', fontSize: 20, cursor: 'pointer' },

  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.5)', zIndex: 10, maxHeight: 280, overflow: 'auto' },
  dropItem: { padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${T.border}`, color: T.ink, fontSize: 13 },
  dropMeta: { color: T.inkMuted, fontSize: 11.5, marginTop: 2, fontFamily: T.mono },

  svcTable: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginTop: 18, border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'hidden' },
  svcTh: { background: T.surface2, color: T.inkDim, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, textAlign: 'left', padding: '11px 14px', fontWeight: 600, borderBottom: `1px solid ${T.border}` },
  svcTd: { padding: '12px 14px', borderBottom: `1px solid ${T.border}`, verticalAlign: 'middle' },

  cellInput: { background: T.surface2, color: T.ink, border: `1px solid ${T.border}`, borderRadius: 4, padding: '7px 10px', fontSize: 13, fontFamily: T.sans, width: '100%', outline: 'none' },
  optStyle: { background: '#1c2230', color: '#e8e6e0', padding: '6px 10px' },
  qtyInput: { background: T.surface2, color: T.accent, border: `1px solid ${T.borderStrong}`, borderRadius: 4, padding: '9px 12px', fontSize: 20, fontFamily: T.sans, fontWeight: 700, textAlign: 'right', width: '100%', outline: 'none', boxSizing: 'border-box', minWidth: 110, fontVariantNumeric: 'tabular-nums' },
  priceInput: { background: 'transparent', color: T.ink, border: `1px solid ${T.border}`, borderRadius: 4, padding: '8px 10px', fontSize: 13, fontFamily: T.sans, textAlign: 'right', width: '100%', outline: 'none', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' },
  lineTotalCell: { textAlign: 'right', fontSize: 15, fontWeight: 600, color: T.ink, fontFamily: T.sans, fontVariantNumeric: 'tabular-nums' },

  rmBtn: { background: 'transparent', color: T.inkMuted, border: 'none', fontSize: 22, cursor: 'pointer', padding: '0 6px' },
  addBtn: { background: 'transparent', color: T.accent, border: `1px dashed ${T.accent}`, borderRadius: 6, padding: '10px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', marginTop: 12, letterSpacing: 0.3 },
  addBtnDisabled: { color: T.inkMuted, borderColor: T.border, cursor: 'not-allowed', opacity: 0.6 },

  footerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 20, paddingTop: 18, borderTop: `1px solid ${T.border}`, gap: 24, flexWrap: 'wrap' },
  totals: { fontSize: 13, color: T.inkDim },
  tLbl: { color: T.inkMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginRight: 10, display: 'inline-block', minWidth: 150 },
  tVal: { color: T.ink, fontFamily: T.mono },
  grand: { marginTop: 10, fontSize: 26, fontWeight: 700, color: T.accent, fontFamily: T.display },
  grandLbl: { color: T.inkMuted, fontSize: 10.5, letterSpacing: 1.6, textTransform: 'uppercase', marginBottom: 4, fontFamily: T.sans, fontWeight: 600 },

  saveBtn: { background: T.accent, color: T.accentInk, border: 'none', padding: '14px 42px', borderRadius: 6, fontFamily: T.display, fontSize: 14, fontWeight: 700, cursor: 'pointer', letterSpacing: 2, textTransform: 'uppercase' },
  saveBtnDisabled: { background: T.surface3, color: T.inkMuted, cursor: 'not-allowed' },
  saveAmt: { opacity: 0.75, fontWeight: 500, marginLeft: 12, letterSpacing: 0.5, fontFamily: T.mono },

  hintBox: { marginTop: 16, padding: '10px 14px', background: 'rgba(245,158,11,0.08)', color: T.accent, borderLeft: `3px solid ${T.accent}`, borderRadius: 3, fontSize: 12.5 },

  addCustBtn: { background: T.surface2, color: T.accent, border: `1px solid ${T.accent}`, borderRadius: 5, padding: '0 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: T.sans },

  // New Customer modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: 16 },
  modal: { background: T.surface1, border: `1px solid ${T.border}`, borderRadius: 8, width: '100%', maxWidth: 520, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.5)' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${T.border}` },
  modalX: { background: 'transparent', color: T.inkMuted, border: 'none', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 },
  modalBody: { padding: '18px 20px', overflowY: 'auto' },
  modalFoot: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: `1px solid ${T.border}` },
  btnSecondary: { background: 'transparent', color: T.inkDim, border: `1px solid ${T.border}`, borderRadius: 5, padding: '10px 20px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: T.sans },
  btnPrimary: { background: T.accent, color: T.accentInk, border: 'none', borderRadius: 5, padding: '10px 28px', fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: 1, textTransform: 'uppercase', fontFamily: T.sans },
  btnDisabled: { background: T.surface3, color: T.inkMuted, cursor: 'not-allowed' },

  // phone-only service card
  svcCard: { background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 6, padding: 14 },
  svcCardHdr: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${T.border}` },
  svcCardIdx: { fontSize: 11, color: T.inkDim, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 },
  svcCardTotal: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, paddingTop: 10, borderTop: `1px dashed ${T.border}` },
};

export default TransportPOS;
